import React from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Tooltip,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useKPIStore } from '../../stores/kpi.store';
import { getStatAccent } from './statVisual';

interface KPIProgressCardProps {
  title: string;
  value: number;
  unit: string;
}

const KPIProgressCard: React.FC<KPIProgressCardProps> = ({
  title,
  value,
  unit,
}) => {
  const theme = useTheme();
  const { limits } = useKPIStore();
  const { Icon, accent } = getStatAccent(theme, title, unit);
  
  // Find the KPI limit for this statistic
  const kpiLimit = limits.find(limit => {
    // Convert both to uppercase and remove spaces/underscores for comparison
    const normalizedTitle = title.replace(/[\s_]/g, '').toUpperCase();
    const normalizedLimitName = limit.name.replace(/[\s_]/g, '').toUpperCase();
    return normalizedLimitName === normalizedTitle && limit.isEnabled;
  });

  if (!kpiLimit) return null;

  const getProgressColor = (value: number, limit: number, operator: string) => {
    switch (operator) {
      case '>':
        return value >= limit ? theme.palette.success.main : theme.palette.error.main;
      case '<':
        return value <= limit ? theme.palette.success.main : theme.palette.error.main;
      case '>=':
        return value >= limit ? theme.palette.success.main : theme.palette.error.main;
      case '<=':
        return value <= limit ? theme.palette.success.main : theme.palette.error.main;
      case '=':
        return Math.abs(value - limit) < 0.01 ? theme.palette.success.main : theme.palette.error.main;
      default:
        return accent;
    }
  };

  const getProgressValue = (value: number, limit: number, operator: string) => {
    switch (operator) {
      case '>':
      case '>=':
        return Math.min((value / limit) * 100, 100);
      case '<':
      case '<=':
        return Math.min((value / limit) * 100, 100);
      case '=':
        return Math.abs(value - limit) < 0.01 ? 100 : 0;
      default:
        return 0;
    }
  };

  const getProgressLabel = (value: number, limit: number, operator: string) => {
    switch (operator) {
      case '>':
        return `${value.toLocaleString()} > ${limit.toLocaleString()} ${unit}`;
      case '<':
        return `${value.toLocaleString()} < ${limit.toLocaleString()} ${unit}`;
      case '>=':
        return `${value.toLocaleString()} ≥ ${limit.toLocaleString()} ${unit}`;
      case '<=':
        return `${value.toLocaleString()} ≤ ${limit.toLocaleString()} ${unit}`;
      case '=':
        return `${value.toLocaleString()} = ${limit.toLocaleString()} ${unit}`;
      default:
        return '';
    }
  };

  const progress = getProgressValue(value, kpiLimit.value, kpiLimit.operator);
  const color = getProgressColor(value, kpiLimit.value, kpiLimit.operator);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        borderRadius: '14px',
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        transition: 'border-color 0.2s ease',
        '&:hover': {
          borderColor: alpha(theme.palette.text.primary, 0.18),
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Icon sx={{ fontSize: 22, color: accent }} />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            textAlign: 'start',
            color: theme.palette.text.secondary,
          }}
        >
          {title.replace(/_/g, ' ')}
        </Typography>
      </Box>

      <Box sx={{ position: 'relative', display: 'inline-flex', mb: 1.5, alignSelf: 'center' }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={88}
          thickness={4}
          sx={{ color: alpha(theme.palette.text.primary, 0.08) }}
        />
        <CircularProgress
          variant="determinate"
          value={progress}
          size={88}
          thickness={4}
          sx={{
            color,
            position: 'absolute',
            left: 0,
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            },
          }}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="h6" component="div" sx={{ fontWeight: 700, color }}>
            {`${Math.round(progress)}%`}
          </Typography>
        </Box>
      </Box>

      <Tooltip title={kpiLimit.alertMessage}>
        <Typography variant="body2" color="text.secondary" align="center">
          {getProgressLabel(value, kpiLimit.value, kpiLimit.operator)}
        </Typography>
      </Tooltip>
    </Paper>
  );
};

export default KPIProgressCard; 